import axios, { AxiosResponse } from 'axios';

import { FileDto } from '@tallo/file-manager';
import {
  PropertyLeaseConditionsDto,
  PropertyLocationDto,
  AccessibilityDto,
  AmenitiesDto,
  IncludedUtilitiesDto,
  ParkingDto,
  PetPolicyDto,
  PropertyDto,
  PropertyRenterRequirementsDto,
  PropertySpecificationsDto,
} from '@tallo/property';

import { PortfolioDto } from '../dtos/portfolio.dto.interface';
import { PropertyStatisticsDto } from '../dtos/property-statistics.dto.interface';
import { SaveAccessibilityDto } from '../dtos/save-accessibility.dto.interface';
import { SaveAmenitiesDto } from '../dtos/save-amenities.dto.interface';
import { SaveIncludedUtilitiesDto } from '../dtos/save-included-utilities.dto.interface';
import { SaveLeaseConditionsDto } from '../dtos/save-lease-condition.dto.interface';
import { SaveLocationDto } from '../dtos/save-location.dto.interface';
import { SaveParkingDto } from '../dtos/save-parking.dto.interface';
import { SavePetPolicyDto } from '../dtos/save-pet-policy.dto.interface';
import { SavePropertySpecificationsDto } from '../dtos/save-property-specifications.dto.interface';
import { SavePropertyRenterRequirementsDto } from '../dtos/save-renter-requirements.dto.interface';

class PropertyService {
  async create(location: Partial<Omit<PropertyLocationDto, 'id'>>): Promise<AxiosResponse<PropertyDto>> {
    return await axios.post<PropertyDto>('property', { location });
  }

  async getById(id: string): Promise<AxiosResponse<PropertyDto>> {
    return await axios.get<PropertyDto>(`property/${id}`);
  }

  async getPropertyStatistics(propertyId: string, filter: string): Promise<PropertyStatisticsDto> {
    const response = await axios.get(`property/statistics/${propertyId}`, {
      params: {
        filter,
      },
    });

    return response.data;
  }

  async getPortfolio(page: number, limit: number): Promise<PortfolioDto> {
    const { data } = await axios.get<PortfolioDto>('property/portfolio', {
      params: {
        page,
        limit,
      },
    });

    return data;
  }

  async updateProperty(propertyId: string, property: Partial<PropertyDto>): Promise<AxiosResponse> {
    return await axios.put(`property/${propertyId}`, property);
  }

  async generateDescription(propertyId: string, highlights?: string): Promise<AxiosResponse<string>> {
    return await axios.put(`property/${propertyId}/description/generate`, {
      highlights,
    });
  }

  async uploadImage(propertyId: string, file: File): Promise<AxiosResponse> {
    const formData = new FormData();
    formData.append('image', file);

    return await axios.post(`property/image/${propertyId}`, formData);
  }

  async updateImagesOrder(propertyId: string, images: FileDto[]): Promise<AxiosResponse> {
    return await axios.put(`property/image/order/${propertyId}`, images);
  }

  async setImageAsCover(propertyId: string, imageId: string) {
    return await axios.put(`property/image/cover/${propertyId}/${imageId}`);
  }

  async deleteImage(propertyId: string, imageId: string): Promise<AxiosResponse> {
    return await axios.delete(`property/image/${propertyId}/${imageId}`);
  }

  async startListing(propertyId: string): Promise<AxiosResponse<PropertyDto>> {
    return await axios.put(`property/publish/${propertyId}`);
  }

  async stopListing(propertyId: string): Promise<AxiosResponse<PropertyDto>> {
    return await axios.put(`property/pause/${propertyId}`);
  }

  async markAsRentedOut(propertyId: string): Promise<AxiosResponse<PropertyDto>> {
    return await axios.put(`property/complete/${propertyId}`);
  }

  async createPetPolicy(propertyId: string, payload: SavePetPolicyDto): Promise<AxiosResponse<PetPolicyDto>> {
    return await axios.post(`property/${propertyId}/pet-policy`, payload);
  }

  async updatePetPolicy(
    propertyId: string,
    petPolicyId: string,
    payload: SavePetPolicyDto,
  ): Promise<AxiosResponse<PetPolicyDto>> {
    return await axios.patch(`property/${propertyId}/pet-policy/${petPolicyId}`, payload);
  }

  async createParking(propertyId: string, payload: SaveParkingDto): Promise<AxiosResponse<ParkingDto>> {
    return await axios.post(`property/${propertyId}/parking`, payload);
  }

  async updateParking(
    propertyId: string,
    parkingId: string,
    payload: SaveParkingDto,
  ): Promise<AxiosResponse<ParkingDto>> {
    return await axios.patch(`property/${propertyId}/parking/${parkingId}`, payload);
  }

  async createRenterRequirements(
    propertyId: string,
    payload: SavePropertyRenterRequirementsDto,
  ): Promise<AxiosResponse<PropertyRenterRequirementsDto>> {
    return await axios.post(`property/${propertyId}/renter-requirements`, payload);
  }

  async updateRenterRequirements(
    propertyId: string,
    renterRequirementsId: string,
    payload: SavePropertyRenterRequirementsDto,
  ): Promise<AxiosResponse<PropertyRenterRequirementsDto>> {
    return await axios.patch(`property/${propertyId}/renter-requirements/${renterRequirementsId}`, payload);
  }

  async createIncludedUtilities(
    propertyId: string,
    payload: SaveIncludedUtilitiesDto,
  ): Promise<AxiosResponse<IncludedUtilitiesDto>> {
    return await axios.post(`property/${propertyId}/included-utilities`, payload);
  }

  async updateIncludedUtilities(
    propertyId: string,
    includedUtilitiesId: string,
    payload: SaveIncludedUtilitiesDto,
  ): Promise<AxiosResponse<IncludedUtilitiesDto>> {
    return await axios.patch(`property/${propertyId}/included-utilities/${includedUtilitiesId}`, payload);
  }

  async createAccessibility(
    propertyId: string,
    payload: SaveAccessibilityDto,
  ): Promise<AxiosResponse<AccessibilityDto>> {
    return await axios.post(`property/${propertyId}/accessibility`, payload);
  }

  async updateAccessibility(
    propertyId: string,
    accessibilityId: string,
    payload: SaveAccessibilityDto,
  ): Promise<AxiosResponse<AccessibilityDto>> {
    return await axios.patch(`property/${propertyId}/accessibility/${accessibilityId}`, payload);
  }

  async createSpecifications(
    propertyId: string,
    payload: SavePropertySpecificationsDto,
  ): Promise<AxiosResponse<PropertySpecificationsDto>> {
    return await axios.post(`property/${propertyId}/specifications`, payload);
  }

  async updateSpecifications(
    propertyId: string,
    specificationId: string,
    payload: SavePropertySpecificationsDto,
  ): Promise<AxiosResponse<PropertySpecificationsDto>> {
    return await axios.patch(`property/${propertyId}/specifications/${specificationId}`, payload);
  }

  async createLeaseConditions(
    propertyId: string,
    payload: SaveLeaseConditionsDto,
  ): Promise<AxiosResponse<PropertyLeaseConditionsDto>> {
    return await axios.post(`property/${propertyId}/lease-conditions`, payload);
  }

  async updateLeaseConditions(
    propertyId: string,
    leaseConditionId: string,
    payload: SaveLeaseConditionsDto,
  ): Promise<AxiosResponse<PropertyLeaseConditionsDto>> {
    return await axios.patch(`property/${propertyId}/lease-conditions/${leaseConditionId}`, payload);
  }

  async createAmenities(propertyId: string, payload: SaveAmenitiesDto): Promise<AxiosResponse<AmenitiesDto>> {
    return await axios.post(`property/${propertyId}/amenities`, payload);
  }

  async updateAmenities(
    propertyId: string,
    amenitiesId: string,
    payload: SaveAmenitiesDto,
  ): Promise<AxiosResponse<AmenitiesDto>> {
    return await axios.patch(`property/${propertyId}/amenities/${amenitiesId}`, payload);
  }

  async updateLocation(
    propertyId: string,
    locationId: string,
    payload: SaveLocationDto,
  ): Promise<AxiosResponse<PropertyLocationDto>> {
    return await axios.patch(`property/${propertyId}/property-location/${locationId}`, payload);
  }

  async delete(propertyId: string): Promise<AxiosResponse<void>> {
    return await axios.delete(`property/${propertyId}`);
  }
}

export default new PropertyService();
